import {GameStatus} from './GameStatus.js';
import {config} from './Config.js';

const colSize = window.innerWidth < 480 ? config.numColsSmall : config.numColsBig

const addCoinToMatrix = (board, col, value) => {
  for (let i = config.numRows - 1; i >= 0; i--) {
    if (!board[i][col]) {
      board[i][col] = value;
      return;
    }
  }
};

const removeLastFilledCoin = (board, col) => {
  for (let i = 0; i < config.numRows; i++) {
    if (board[i][col]) {
      board[i][col] = 0;
      return;
    }
  }
};

const getUnfilledColumns = board => {
  let unfilledColumns = [];
  // for (let i = 0; i < config.numCols; i++) {
  for (let i = 0; i < colSize; i++) {
    if (!board[0][i]) {
      unfilledColumns.push(i);
    }
  }
  return unfilledColumns;
};

const rewardSystem = (board, comPlayer, userPlayer) => {
  /*
    1. generate windows of length  4 
    2. Based on different configuration of opponent and AI, either reward or punish
    */

  const isWithinFrameBoundary = (row, col) => {
    // return row >= 0 && col >= 0 && row < config.numRows && col < config.numCols;
    return row >= 0 && col >= 0 && row < config.numRows && col < colSize;
  };

  const windowScore = window => {
    let score = 0;

    const freq = {};
    for (const num of window) {
      freq[num] = freq[num] ? freq[num] + 1 : 1;
    }

    const comCount = freq[comPlayer] ? freq[comPlayer] : 0;
    const playerCount = freq[userPlayer] ? freq[userPlayer] : 0;
    const emptyCount = freq[0] ? freq[0] : 0;

    if (comCount === 4) {
      score += 800;
    }
    if (comCount === 3 && emptyCount === 1) {
      score += 600;
    }
    if (comCount === 2 && emptyCount === 2) {
      score += 400;
    }
    if (comCount === 3 && playerCount === 1) {
      score -= 100;
    }
    if (playerCount === 3 && emptyCount === 1) {
      score -= 600;
    }
    if (playerCount === 2 && emptyCount === 2) {
      score -= 400;
    }

    // Special Case: AI is trying to block the player
    // hence give AI a good reward
    if (playerCount === 3 && comCount === 1) {
      score += 300;
    }

    return score;
  };

  let score = 0;

  for (let i = 0; i < config.numRows; i++) {
    // for (let j = 0; j < config.numCols; j++) {
    for (let j = 0; j < colSize; j++) {
      const possibleStepsWindow = [
        [
          [i, j],
          [i, j + 1],
          [i, j + 2],
          [i, j + 3],
        ],
        [
          [i, j],
          [i + 1, j],
          [i + 2, j],
          [i + 3, j],
        ],
        [
          [i, j],
          [i + 1, j + 1],
          [i + 2, j + 2],
          [i + 3, j + 3],
        ],
        [
          [i, j],
          [i + 1, j - 1],
          [i + 2, j - 2],
          [i + 3, j - 3],
        ],
      ];

      for (const windowPosition of possibleStepsWindow) {
        let isWithinFrame = true;
        for (const [row, col] of windowPosition) {
          if (!isWithinFrameBoundary(row, col)) {
            isWithinFrame = false;
            break;
          }
        }

        if (!isWithinFrame) {
          continue;
        }

        const window = windowPosition.map(
          position => board[position[0]][position[1]]
        );
        score += windowScore(window);
      }
    }
  }

  return score;
};

export const miniMax = (
  board,
  depth,
  alpha,
  beta,
  isMaximizingPlayer,
  comPlayer,
  userPlayer,
  MAX_DEPTH
) => {
  const gameStatus = GameStatus(
    board,
    colSize,
    // config.numCols,
    config.numRows,
    config.winCount
  ).status;
  if (gameStatus !== 0) {
    if (gameStatus === comPlayer) {
      return [config.AI_WIN_REWARD, -1];
    } else if (gameStatus === userPlayer) {
      return [config.PLAYER_WIN_PUNISHMENT, -1];
    } else {
      return [0, -1];
    }
  }

  const unfilledColumns = getUnfilledColumns(board);

  if (depth >= MAX_DEPTH) {
    const randomIndex = Math.floor(Math.random() * unfilledColumns.length);
    const result = [
      rewardSystem(board, comPlayer, userPlayer),
      unfilledColumns[randomIndex],
    ];
    return result;
  }

  if (isMaximizingPlayer) {
    let bestScore = Number.NEGATIVE_INFINITY;
    const randomIndex = Math.floor(Math.random() * unfilledColumns.length);
    let bestColumn = unfilledColumns[randomIndex];

    for (const col of unfilledColumns) {
      addCoinToMatrix(board, col, comPlayer);
      const [currentScore, bestOpponentCol] = miniMax(
        board,
        depth + 1,
        alpha,
        beta,
        false,
        comPlayer,
        userPlayer,
        MAX_DEPTH
      );
      if (currentScore > bestScore) {
        bestScore = currentScore;
        bestColumn = col;
      }
      alpha = Math.max(alpha, bestScore);
      removeLastFilledCoin(board, col);

      if (beta <= alpha) {
        break;
      }
    }

    const result = [bestScore, bestColumn];
    return result;
  } else {
    let bestScore = Number.POSITIVE_INFINITY;
    const randomIndex = Math.floor(Math.random() * unfilledColumns.length);
    let bestColumn = unfilledColumns[randomIndex];

    for (const col of unfilledColumns) {
      addCoinToMatrix(board, col, userPlayer);
      const [currentScore, bestOpponentCol] = miniMax(
        board,
        depth + 1,
        alpha,
        beta,
        true,
        comPlayer,
        userPlayer,
        MAX_DEPTH
      );

      if (currentScore < bestScore) {
        bestScore = currentScore;
        bestColumn = col;
      }
      beta = Math.min(beta, bestScore);
      removeLastFilledCoin(board, col);

      if (beta <= alpha) {
        break;
      }
    }

    const result = [bestScore, bestColumn];
    return result;
  }
};
